import { storableError } from '../../util/errors';
import { getCategoryCounts, getFacets } from '../../util/api';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { isFieldForCategory } from '../../util/fieldHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  resolveCategoryFromUrl,
  getCategoryFilterParams,
} from '../../util/categoryLandingHelpers';
import { validUrlQueryParamsFromProps } from '../SearchPage/SearchPage.shared';
import { fetchExceptFieldPresence } from '../SearchPage/SearchPage.duck';
import { isFilterActive } from '../SearchPage/FilterBar/filterBarHelpers';

// Pagination page size
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SET_CATEGORY = 'app/CategoryPage/SET_CATEGORY';

export const SEARCH_LISTINGS_REQUEST = 'app/CategoryPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/CategoryPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/CategoryPage/SEARCH_LISTINGS_ERROR';

export const FETCH_TREE_COUNTS_SUCCESS = 'app/CategoryPage/FETCH_TREE_COUNTS_SUCCESS';

export const FETCH_FACETS_SUCCESS = 'app/CategoryPage/FETCH_FACETS_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  category: null,
  breadcrumbPath: [],
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  // The latest search asked for: an older answer arriving after it is dropped.
  searchRequest: 0,
  // Per-category listing totals for the Shop by Category column
  // ({ categoryId: count } or null when the endpoint degraded)
  treeCounts: null,
  // v1 facets: { total, scanned, exact, presence, categoryCounts } or null
  facets: null,
};

const resultIds = data => data.data.map(l => l.id);

const categoryPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_CATEGORY:
      return {
        ...state,
        category: payload.category,
        breadcrumbPath: payload.breadcrumbPath,
      };

    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchRequest: payload.request,
        searchInProgress: true,
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return action.meta?.request !== state.searchRequest
        ? state
        : {
            ...state,
            currentPageResultIds: resultIds(payload.data),
            pagination: payload.data.meta,
            searchInProgress: false,
          };
    case SEARCH_LISTINGS_ERROR:
      if (action.meta?.request !== state.searchRequest) return state;
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case FETCH_TREE_COUNTS_SUCCESS:
      return { ...state, treeCounts: payload };

    case FETCH_FACETS_SUCCESS:
      return { ...state, facets: payload };

    default:
      return state;
  }
};

export default categoryPageReducer;

// ================ Selectors ================ //

export const selectCategory = state => state.CategoryPage.category;
export const selectBreadcrumbPath = state => state.CategoryPage.breadcrumbPath;
export const selectSearchInProgress = state => state.CategoryPage.searchInProgress;
export const selectPagination = state => state.CategoryPage.pagination;
export const selectCurrentPageResultIds = state => state.CategoryPage.currentPageResultIds;

// ================ Action creators ================ //

export const setCategory = (category, breadcrumbPath) => ({
  type: SET_CATEGORY,
  payload: { category, breadcrumbPath },
});

export const searchListingsRequest = (searchParams, request) => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams, request },
});

export const searchListingsSuccess = (response, request) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
  meta: { request },
});

export const searchListingsError = (e, request) => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

// ================ Thunks ================ //

let searchRequests = 0;
export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  searchRequests += 1;
  const request = searchRequests;
  dispatch(searchListingsRequest(searchParams, request));

  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  // showSoldOut is a UI-only URL param (same mapping as SearchPage.duck):
  // active → pub_soldOut=true (ONLY sold listings), else stock semantics.
  const stockFilters = showSoldOut => {
    if (showSoldOut === true || showSoldOut === 'true') {
      return { pub_soldOut: true };
    }
    return { minStock: 1, stockMode: 'match-undefined' };
  };

  const { perPage, price, sort, showSoldOut, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const stockMaybe = stockFilters(showSoldOut);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : sort ? { sort } : {};

  const params = {
    ...rest,
    ...priceMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response, request));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e), request));
      throw e;
    });
};

/**
 * The category a URL names: /{url_slug}, or /{ancestor slug}/{url_slug} (the
 * hierarchical form, which the page sends on to /{url_slug}). Null for anything else.
 */
export const resolveCategoryFromParams = params => {
  const { slug, slug1, slug2 } = params || {};
  if (slug) return resolveCategoryFromUrl([slug]);
  if (!slug1 || !slug2) return null;

  const resolved = resolveCategoryFromUrl([slug2]);
  const ancestors = resolved ? resolved.breadcrumbPath.slice(0, -1) : [];
  return ancestors.some(cat => cat.url_slug === slug1) ? resolved : null;
};

/**
 * Load data for CategoryPage
 * Resolves category from URL and fetches listings
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const resolved = resolveCategoryFromParams(params);

  if (!resolved) {
    // No matching category: the page shows not found (HTTP 404 on the server render).
    // Resolves quietly so dataLoader doesn't log an error.
    dispatch(setCategory(null, []));
    return Promise.resolve();
  }

  const { category, breadcrumbPath } = resolved;

  // Set category in state
  dispatch(setCategory(category, breadcrumbPath));

  // Parse additional search params from query string
  const queryParams = parse(search);
  const { page = 1, price, sort, ...rest } = queryParams;
  const activeFilters = { price, sort, ...rest }; // user-applied filters (raw, pre-conversion)

  // Get category filter params
  const categoryFilters = getCategoryFilterParams(category, breadcrumbPath);

  const searchParams = {
    ...categoryFilters,
    ...rest,
    price,
    sort,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    ...createImageVariantConfig('listing-card', 400, 1),
    ...createImageVariantConfig('listing-card-2x', 800, 1),
    'limit.images': 5,
  };

  // Each filtered field's own option list, ready for when its pill or panel opens. The
  // params match the page's facetsParams (category scope + the URL's filters).
  const urlQueryParams = validUrlQueryParamsFromProps({ location: { search }, config });
  const facetsParams = { ...categoryFilters, ...urlQueryParams };
  const categoryIds = breadcrumbPath.map(c => c.id);
  const { listingFields: listingFieldsConfig = [] } = config?.listing || {};
  listingFieldsConfig
    .filter(
      field =>
        field.filterConfig?.indexForSearch &&
        field.enumOptions?.length > 0 &&
        isFieldForCategory(categoryIds, field) &&
        isFilterActive(field, urlQueryParams)
    )
    .forEach(field => dispatch(fetchExceptFieldPresence(facetsParams, field)));

  return Promise.all([
    dispatch(searchListings(searchParams, config)),
    dispatch(fetchCategoryTreeCounts()),
    dispatch(fetchFacets({ ...categoryFilters, ...activeFilters })),
  ]).then(([response]) => response);
};

/**
 * Fetch v1 facets (presence) for the filter panel. Raw UI params go through
 * verbatim — the server does the price/soldOut/stock conversions. Failure
 * leaves facets null and the panel falls back to full option lists.
 */
// /api/facets scans up to 5 upstream pages, so a slow earlier response can
// land AFTER a newer one during rapid filter changes; only the latest request
// is allowed to write state (mirrors searchParamsAreInSync for listings).
let latestFacetsRequest = 0;
export const fetchFacets = rawParams => dispatch => {
  const requestId = ++latestFacetsRequest;
  const cleaned = Object.keys(rawParams || {}).reduce((acc, key) => {
    const value = rawParams[key];
    if (value !== undefined && value !== null) acc[key] = value;
    return acc;
  }, {});
  return Promise.resolve()
    .then(() => getFacets(cleaned))
    .then(response => {
      if (requestId !== latestFacetsRequest) return;
      dispatch({ type: FETCH_FACETS_SUCCESS, payload: response || null });
    })
    .catch(() => {
      if (requestId !== latestFacetsRequest) return;
      dispatch({ type: FETCH_FACETS_SUCCESS, payload: null });
    });
};

/**
 * Fetch per-category listing totals for the Shop by Category column.
 * Server caches the sweep; a failure just leaves treeCounts as null and the
 * column renders without numbers.
 */
export const fetchCategoryTreeCounts = () => dispatch => {
  return Promise.resolve()
    .then(() => getCategoryCounts())
    .then(response => {
      dispatch({ type: FETCH_TREE_COUNTS_SUCCESS, payload: response?.counts || null });
    })
    .catch(() => {
      dispatch({ type: FETCH_TREE_COUNTS_SUCCESS, payload: null });
    });
};
